import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { MaterialModule } from './modules/material/material.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { ToolbarComponent } from './components/toolbar/toolbar.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RunLogComponent } from './components/run-log/run-log.component';
import { RunDataComponent } from './components/run-data/run-data.component';
import { ActionComponent } from './components/action/action.component';
import { ErrorsComponent } from './components/errors/errors.component';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TabulatorUserTableComponent } from './components/tabulator-user-table/tabulator-user-table.component';
import { TabulatorTableComponent } from './components/tabulator-table/tabulator-table.component';
import { DataLakeComponent } from './components/data-lake/data-lake.component';
import { IntegrationHistoryComponent } from './components/integration-history/integration-history.component';
import { IntegrationStatusComponent } from './components/integration-status/integration-status.component';
import { DataStudioComponent } from './components/data-studio/data-studio.component';
import { IntegrationDashboardComponent } from './components/integration-dashboard/integration-dashboard.component';
import { UserCardComponent } from './components/user-card/user-card.component';
import { VirtualizedDateListComponent } from './components/virtualized-date-list/virtualized-date-list.component';
import { ModalModule } from './modal';
import { DragNDrop } from './components/dropbox/drag-n-drop';
import { ProgressComponent } from './components/dropbox/progress/progress.component';
import { ToastComponent } from './components/toast/toast.component';
import { ToasterComponent } from './components/toaster/toaster.component';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { InterceptorService } from './services/interceptor.service';
import { AuthConfigModule } from './auth/auth-config.module';
import { HttpOIDCInterceptorService } from './services/http-oidc-interceptor.service';
import { AppRoutingModule } from './app-routing.module';
import { AdminComponent } from './components/admin/admin.component';
import { HttpCacheInterceptorService } from './services/http-cache-interceptor.service';
import { PermissionsIntegrationComponent } from './components/permissions-integration/permissions-integration.component';
import { CollapsibleComponent } from './components/collapsible/collapsible.component';
import { PermissionsTableComponent } from './components/permissions-table/permissions-table.component';
import { ConfigService } from './services/config.service';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { GlobalErrorHandlerService } from './services/global-error-handler.service';
import { ClientErrorInterceptorService } from './services/client-error-interceptor.service';

export function initializeApp(configService: ConfigService) {
  return () => configService.load();
}

@NgModule({ 
    declarations: [
        AppComponent,
        PageNotFoundComponent,
        DashboardComponent,
        ToolbarComponent,
        RunLogComponent,
        RunDataComponent,
        ActionComponent,
        ErrorsComponent,
        TabulatorUserTableComponent,
        TabulatorTableComponent,
        DataLakeComponent,
        VirtualizedDateListComponent,
        IntegrationHistoryComponent,
        IntegrationStatusComponent,
        DataStudioComponent,
        IntegrationDashboardComponent,
        UserCardComponent,
        ProgressComponent,
        DragNDrop,
        ToastComponent,
        ToasterComponent,
        SideNavComponent,
        AdminComponent,
        UnauthorizedComponent
    ],
    bootstrap: [AppComponent], 
    imports: [
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        MaterialModule,
        FormsModule,
        ReactiveFormsModule,
        ModalModule,
        AuthConfigModule,
        PermissionsIntegrationComponent,
        PermissionsTableComponent,
        CollapsibleComponent
    ], 
    providers: [
        ConfigService,
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideAppInitializer(() => {
          const initializerFn = (initializeApp)(inject(ConfigService));
          return initializerFn();
        }),
        { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
        { provide: HTTP_INTERCEPTORS, useClass: HttpOIDCInterceptorService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: InterceptorService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ClientErrorInterceptorService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpCacheInterceptorService, multi: true },
        provideHttpClient(withInterceptorsFromDi())
    ] 
})

export class AppModule { }
